import React, { useState, useEffect } from "react";
import { Music, Play, Pause, SkipForward, Volume2, VolumeX } from "lucide-react";
import { useSound } from "@/context/SoundContext";
import { TID } from "@/lib/testIds";

const MusicPlayer = () => {
  const { available, track, playing, muted, volume, setVolume, setMuted, toggle, next, hasPlaylist } = useSound();
  const [open, setOpen] = useState(false);
  const [consented, setConsented] = useState(() => { try { return !!JSON.parse(localStorage.getItem("nba_consent")); } catch { return false; } });
  useEffect(() => {
    const h = () => setConsented(true);
    window.addEventListener("nba-consent", h);
    return () => window.removeEventListener("nba-consent", h);
  }, []);
  if (!available) return null;

  return (
    <div className={`fixed left-5 z-30 select-none transition-[bottom] duration-500 ${consented ? "bottom-5" : "bottom-40 md:bottom-24"}`} style={{ pointerEvents: "auto" }}>
      {open ? (
        <div className="bg-cocoa text-cream rounded-md editorial-shadow px-4 py-3 w-64 fade-up">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-end gap-[3px] h-4">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="sound-bar" style={{ animationDelay: `${i * 0.15}s`, animationPlayState: playing ? "running" : "paused", height: playing ? undefined : "4px" }} />
              ))}
            </div>
            <button onClick={() => setOpen(false)} className="text-cream/50 text-xs tracking-widest uppercase">Close</button>
          </div>
          <p className="font-serif text-lg leading-tight mb-3 truncate">{track?.title || "NoirbyAmara"}</p>
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid={TID.musicPlayPause} aria-label={playing ? "Pause" : "Play"}
              className="w-9 h-9 rounded-full border border-cream/30 flex items-center justify-center hover:bg-cream hover:text-cocoa transition-colors">
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
            {hasPlaylist && (
              <button onClick={next} data-testid="music-next" aria-label="Next song" className="text-cream/70 hover:text-cream">
                <SkipForward size={17} />
              </button>
            )}
            <button onClick={() => setMuted(!muted)} data-testid={TID.musicMute} aria-label="Mute" className="text-cream/70 hover:text-cream">
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
            <input type="range" min="0" max="1" step="0.05" value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))} aria-label="Volume"
              className="flex-1 accent-gold h-1" />
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid={TID.musicToggle} aria-label="Sound control"
          className="w-12 h-12 rounded-full bg-cocoa text-cream editorial-shadow flex items-center justify-center hover:scale-105 transition-transform">
          {playing ? (
            <span className="flex items-end gap-[2px] h-4">
              {[0, 1, 2].map((i) => <span key={i} className="sound-bar" style={{ animationDelay: `${i * 0.18}s` }} />)}
            </span>
          ) : <Music size={18} strokeWidth={1.5} />}
        </button>
      )}
    </div>
  );
};

export default MusicPlayer;
